import { useState, useEffect, Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'

// Lazy load all page components for better performance
const QuoteBuilder = lazy(() => import('./pages/QuoteBuilder'))
const Home = lazy(() => import('./pages/Home'))
const QuotePDFPreview = lazy(() => import('./pages/QuotePDFPreview'))
const ModelDetail = lazy(() => import('./pages/ModelDetail'))
const Configure = lazy(() => import('./pages/checkout/Configure'))
const Confirm = lazy(() => import('./pages/checkout/Confirm'))
const FAQPage = lazy(() => import('./pages/FAQ'))
const ModelsPage = lazy(() => import('./pages/Models'))
const PaymentMethod = lazy(() => import('./pages/checkout/PaymentMethod'))
const CashPayment = lazy(() => import('./pages/checkout/CashPayment'))
const Buyer = lazy(() => import('./pages/checkout/Buyer'))
const Review = lazy(() => import('./pages/checkout/Review'))
const ContractNew = lazy(() => import('./pages/checkout/ContractNew'))
const DocumentSigned = lazy(() => import('./pages/checkout/DocumentSigned'))
const AccountCreate = lazy(() => import('./pages/checkout/Account'))
const HowItWorks = lazy(() => import('./pages/HowItWorks'))
const ParkModelResorts = lazy(() => import('./pages/ParkModelResorts'))
const Ordering = lazy(() => import('./pages/how/Ordering'))
const Delivery = lazy(() => import('./pages/how/Delivery'))
const Warranty = lazy(() => import('./pages/how/Warranty'))
const WhyOnline = lazy(() => import('./pages/how/WhyOnline'))
const PortalOrders = lazy(() => import('./pages/portal/Orders'))
const PortalDashboard = lazy(() => import('./pages/portal/Dashboard'))
const AdminOrders = lazy(() => import('./pages/admin/Orders'))
const AdminModels = lazy(() => import('./pages/admin/Models'))
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'))
const AdminOverview = lazy(() => import('./pages/admin/AdminOverview'))
const AdvancedReporting = lazy(() => import('./components/AdvancedReporting'))
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard'))
const AdminCustomers = lazy(() => import('./pages/admin/Customers'))
const AdminDrafts = lazy(() => import('./pages/admin/Drafts'))
const AdminAnalytics = lazy(() => import('./pages/admin/Analytics'))
const AnalyticsEnterprise = lazy(() => import('./pages/admin/AnalyticsEnterprise'))
const RealTimeTracker = lazy(() => import('./components/analytics/RealTimeTracker'))
const PublicModelDetail = lazy(() => import('./public/PublicModelDetail'))
const PackageDetail = lazy(() => import('./public/PackageDetail'))
const BuildsDashboard = lazy(() => import('./pages/builds/Builds'))
const BuildCustomize = lazy(() => import('./pages/builds/Customize'))
const About = lazy(() => import('./pages/About'))
const Team = lazy(() => import('./pages/about/Team'))
const Manufacturer = lazy(() => import('./pages/about/Manufacturer'))
const Contact = lazy(() => import('./pages/Contact'))
const Financing = lazy(() => import('./pages/Financing'))
const Customize = lazy(() => import('./pages/Customize'))
const SignUp = lazy(() => import('./pages/SignUp'))
const SignIn = lazy(() => import('./pages/SignIn'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsConditions = lazy(() => import('./pages/TermsConditions'))
const OtherPolicies = lazy(() => import('./pages/OtherPolicies'))
const AdminPolicies = lazy(() => import('./pages/admin/Policies'))
const AIInsights = lazy(() => import('./pages/admin/AIInsights'))
const AdvancedReports = lazy(() => import('./pages/admin/AdvancedReports'))
const Integrations = lazy(() => import('./pages/admin/Integrations'))
const Security = lazy(() => import('./pages/admin/Security'))
const Workflows = lazy(() => import('./pages/admin/Workflows'))
const Monitoring = lazy(() => import('./pages/admin/Monitoring'))
const DataExport = lazy(() => import('./pages/admin/DataExport'))
const Content = lazy(() => import('./pages/admin/Content'))
const Notifications = lazy(() => import('./pages/admin/Notifications'))
// Change the registration URL when the shell identity/cache generation changes
// so browsers do not continue using a previously registered worker script.
const SERVICE_WORKER_URL = '/sw.js?v=parkmodelprices-v12'
const Financial = lazy(() => import('./pages/admin/Financial'))
const Settings = lazy(() => import('./pages/admin/Settings'))
const Blog = lazy(() => import('./pages/Blog'))
const BlogAll = lazy(() => import('./pages/BlogAll'))
const BlogCreate = lazy(() => import('./pages/BlogCreate'))
const BlogPost = lazy(() => import('./pages/BlogPost'))
const WhyGoTiny = lazy(() => import('./pages/blog/WhyGoTiny'))
const BestSkirtingOptions = lazy(() => import('./pages/blog/BestSkirtingOptions'))
const TexasRegulations = lazy(() => import('./pages/blog/TexasRegulations'))
import { getAllValidSlugs } from './utils/modelUrlMapping'
import { testModelUrls, generateModelSitemap } from './utils/testModelUrls'
import { verifyImplementation } from './utils/verifyImplementation'
import ErrorBoundary from './components/ErrorBoundary'
import FirefliesBackground from './components/FirefliesBackground'
import BackgroundImage from './components/BackgroundImage'
import Header from './components/Header'
import MobileNavigation from './components/MobileNavigation'
import Footer from './components/Footer'
import OfflineIndicator from './components/OfflineIndicator'
import { PageLoadingSpinner } from './components/LoadingSpinner'
import NetworkErrorHandler from './components/NetworkErrorHandler'
import './App.css'
import ProtectedRoute from './components/ProtectedRoute'
import { useGlobalAuthErrorInterceptor } from './components/AuthErrorHandler'
import { useScrollToTop } from './hooks/useScrollToTop'
import CustomizationMigration from './components/CustomizationMigration'
import PerformanceMonitor from './components/PerformanceMonitor'

import SecurityAuditReport from './components/SecurityAuditReport'

import './utils/performance' // Initialize performance monitoring
import './utils/accessibility' // Initialize accessibility features
import { initializeMobileOptimizations } from './utils/mobileOptimizations' // Initialize mobile optimizations

const ENABLE_REALTIME_TRACKER = import.meta.env.DEV || import.meta.env.VITE_ENABLE_ANALYTICS === 'true'

// Component that handles scroll-to-top within Router context
function ScrollToTop() {
  useScrollToTop()
  return null
}

// Component that determines mobile spacing based on route
function MobileSpacingWrapper({ children }) {
  const location = useLocation()
  const isHomePage = location.pathname === '/'
  const isAuthPage = location.pathname.startsWith('/sign-in') || location.pathname.startsWith('/sign-up')
  const isAdminPage = location.pathname.startsWith('/admin')
  
  // Admin pages render their own layout, so don't wrap them
  if (isAdminPage) {
    return <>{children}</>
  }
  
  // Apply mobile-content-spacing to all pages except homepage and auth pages
  const mainClassName = isHomePage || isAuthPage
    ? "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-8"
    : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-8 mobile-content-spacing"
  
  return (
    <main id="main-content" className={mainClassName}>
      {children}
    </main>
  )
}

// Hide public navigation on admin pages; admin pages own their full layout.
function ConditionalHeader() {
  const location = useLocation()
  if (location.pathname.startsWith('/admin')) return null
  return <Header />
}

// Hide public mobile navigation on admin pages; admin pages own their full layout.
function ConditionalMobileNavigation() {
  const location = useLocation()
  if (location.pathname.startsWith('/admin')) return null
  return <MobileNavigation />
}

// Conditional Background - Hidden on admin pages
function ConditionalBackground() {
  const location = useLocation()
  const isAdminPage = location.pathname.startsWith('/admin')
  
  if (isAdminPage) {
    return null
  }
  
  return (
    <>
      <BackgroundImage src="/hero/tiny-home-dusk.png" />
      <FirefliesBackground density={0.12} color="#FFD86B" parallax={0.25} />
    </>
  )
}

// Always show these components - they don't interfere with admin layout
function ConditionalOfflineIndicator() {
  return <OfflineIndicator />
}

function ConditionalCustomizationMigration() {
  return <CustomizationMigration />
}

function ConditionalFooter() {
  const location = useLocation()
  const isAdminPage = location.pathname.startsWith('/admin')
  
  // Footer is hidden on admin pages since admin has its own layout
  if (isAdminPage) {
    return null
  }
  
  return <Footer />
}

function App() {
  const [quoteData, setQuoteData] = useState(null)
  const [selectedModel, setSelectedModel] = useState(null)
  const [dark, setDark] = useState(() => {
    if (typeof window === 'undefined') return true
    const persisted = window.localStorage.getItem('theme')
    return persisted ? persisted === 'dark' : true
  })
  // Initialize global auth error interceptor
  useGlobalAuthErrorInterceptor()

  // Initialize mobile optimizations for smooth viewport handling
  useEffect(() => {
    initializeMobileOptimizations()
  }, [])

  const handleModelSelect = (modelCode) => {
    setSelectedModel(modelCode)
  }

  // Dynamic routing handles all slugs/codes; no need to enumerate

  // Test URLs in development
  useEffect(() => {
    // apply theme on mount and when toggled
    const html = document.documentElement
    if (dark) {
      html.classList.add('dark')
      html.setAttribute('data-theme', 'dark')
      document.body.setAttribute('data-theme', 'dark')
      window.localStorage.setItem('theme', 'dark')
    } else {
      html.classList.remove('dark')
      html.setAttribute('data-theme', 'light')
      document.body.setAttribute('data-theme', 'light')
      window.localStorage.setItem('theme', 'light')
    }

    // Expose helper to unregister service workers if they misbehave
    if (typeof window !== 'undefined' && !window.__UNREGISTER_SW__) {
      window.__UNREGISTER_SW__ = async () => {
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations()
          await Promise.all(registrations.map(reg => reg.unregister()))
          console.log('Service worker: unregistered all service workers')
          window.location.reload()
          return
        }
        console.log('Service worker: service workers are not supported in this browser')
      }
      console.log('Service worker: to unregister service workers, run window.__UNREGISTER_SW__()')
    }

    // Register service worker for caching and offline support
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations()
        .then((registrations) => {
          if (registrations.length) {
            console.log('Service worker: unregistering stale service workers:', registrations.map(r => r.scope))
          }
          return Promise.allSettled(registrations.map(reg => reg.unregister()))
        })
        .finally(() => {
          navigator.serviceWorker.register(SERVICE_WORKER_URL)
            .then((registration) => {
              console.log('Service Worker registered:', registration)
            })
            .catch((error) => {
              console.log('Service Worker registration failed:', error)
            })
        })
    }

    if (import.meta.env.DEV) {
      console.log('Development mode: comprehensive testing...')
      
      // Basic URL testing
      testModelUrls()
      generateModelSitemap()
      
      // Comprehensive verification
      setTimeout(() => {
        console.log('\nRunning comprehensive verification...')
        verifyImplementation()
      }, 1000)
    }
  }, [dark])

  const toggleTheme = () => setDark(v => !v)

  return (
    <ErrorBoundary>
      <NetworkErrorHandler>
        <Router>
          <ScrollToTop />
          <PerformanceMonitor />
          
        {/* Enterprise Real-Time Analytics Tracking - disabled in production unless explicitly enabled */}
        {ENABLE_REALTIME_TRACKER && (
          <Suspense fallback={null}>
            <RealTimeTracker />
          </Suspense>
        )}
          
          <div className="min-h-screen flex flex-col transition-colors duration-300" data-app-container>
            {/* Skip links for accessibility */}
            <a href="#main-content" className="skip-link sr-only-focusable">
              Skip to main content
            </a>
            <a href="#navigation" className="skip-link sr-only-focusable">
              Skip to navigation
            </a>
            
            {/* Background image with gradient + animated fireflies above it - Hidden on admin pages */}
            <ConditionalBackground />
            
            {/* Desktop Navigation - Hidden on admin pages */}
            <div className="hidden md:block">
              <ConditionalHeader />
            </div>
            
            {/* Mobile Navigation - Hidden on admin pages */}
            <div className="md:hidden">
              <ConditionalMobileNavigation />
            </div>

            <div className="flex-1">
              <MobileSpacingWrapper>
                <ConditionalOfflineIndicator />
                <ConditionalCustomizationMigration />
                <Suspense fallback={<PageLoadingSpinner />}>
                  <Routes>
              <Route 
                path="/" 
                element={<Home />} 
              />
              <Route path="/estimator" element={<QuoteBuilder />} />
              <Route path="/models" element={<ModelsPage />} />
              
              {/* Single dynamic route supporting slug or code */}
              <Route 
                path="/models/:id" 
                element={<ModelDetail onModelSelect={handleModelSelect} />} 
              />
              <Route path="/checkout/configure/:slug" element={<AccountCreate />} />
              <Route path="/checkout/payment" element={<PaymentMethod />} />
              <Route path="/checkout/buyer" element={<Buyer />} />
              <Route path="/checkout/review" element={<Review />} />
              <Route path="/how" element={<HowItWorks />} />
              <Route path="/park-model-resorts" element={<ParkModelResorts />} />
              <Route path="/how/ordering" element={<Ordering />} />
              <Route path="/how/delivery" element={<Delivery />} />
              <Route path="/how/warranty" element={<Warranty />} />
              <Route path="/how/why-buy-online" element={<WhyOnline />} />
              {/* Removed legacy steps: Delivery/Auth/Payment/Sign */}
              <Route path="/checkout/confirm" element={<Confirm />} />
              <Route path="/public/models/:id" element={<PublicModelDetail />} />
              <Route path="/public/models/:id/package/:key" element={<PackageDetail />} />
              <Route path="/builds" element={
                <ProtectedRoute>
                  <BuildsDashboard />
                </ProtectedRoute>
              } />
              <Route path="/builds/:buildId" element={
                <ProtectedRoute>
                  <BuildCustomize />
                </ProtectedRoute>
              } />
              <Route path="/checkout/:buildId" element={
                <ProtectedRoute>
                  <Review />
                </ProtectedRoute>
              } />
              <Route path="/checkout/:buildId/payment-method" element={
                <ProtectedRoute>
                  <PaymentMethod />
                </ProtectedRoute>
              } />
              <Route path="/checkout/:buildId/cash-payment/*" element={
                <ProtectedRoute>
                  <CashPayment />
                </ProtectedRoute>
              } />
              <Route path="/checkout/:buildId/buyer" element={
                <ProtectedRoute>
                  <Buyer />
                </ProtectedRoute>
              } />
              <Route path="/checkout/:buildId/review" element={
                <ProtectedRoute>
                  <Review />
                </ProtectedRoute>
              } />
              <Route path="/checkout/:buildId/agreement" element={
                <ProtectedRoute>
                  <ContractNew />
                </ProtectedRoute>
              } />
              <Route path="/checkout/:buildId/confirm" element={
                <ProtectedRoute>
                  <Confirm />
                </ProtectedRoute>
              } />
              <Route path="/checkout/:buildId/document-signed" element={<DocumentSigned />} />
              <Route path="/about" element={<About />} />
              <Route path="/about/team" element={<Team />} />
              <Route path="/about/manufacturer" element={<Manufacturer />} />
                      <Route path="/blog" element={<Blog />} />
        <Route path="/blog/all" element={<BlogAll />} />
        <Route path="/blog/create" element={
          <ProtectedRoute requireAdmin={true}>
            <BlogCreate />
          </ProtectedRoute>
        } />
        <Route path="/blog/edit/:id" element={
          <ProtectedRoute requireAdmin={true}>
            <BlogCreate />
          </ProtectedRoute>
        } />
        <Route path="/blog/why-go-tiny-complete-guide-park-model-living" element={<WhyGoTiny />} />
        <Route path="/blog/best-skirting-options-park-model-homes" element={<BestSkirtingOptions />} />
        <Route path="/blog/park-model-regulations-texas-law" element={<TexasRegulations />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/financing" element={<Financing />} />
              <Route path="/customize/:modelId" element={<Customize />} />
              <Route path="/sign-up" element={<SignUp />} />
              <Route path="/sign-in" element={<SignIn />} />
              
              {/* Auth routes for verification and onboarding flows */}
              <Route path="/sign-up/verify-email-address" element={<SignUp />} />
              <Route path="/sign-up/verify-email-address/*" element={<SignUp />} />
              <Route path="/sign-in/verify-email-address" element={<SignIn />} />
              <Route path="/sign-in/verify-email-address/*" element={<SignIn />} />
              <Route path="/verify-email-address" element={<SignUp />} />
              <Route path="/verify-email-address/*" element={<SignUp />} />
              <Route path="/reset-password" element={<SignIn />} />
              <Route path="/reset-password/*" element={<SignIn />} />
              <Route path="/sign-up/continue" element={<SignUp />} />
              <Route path="/sign-up/continue/*" element={<SignUp />} />
              <Route path="/sign-in/continue" element={<SignIn />} />
              <Route path="/sign-in/continue/*" element={<SignIn />} />
              <Route path="/portal" element={
                <ProtectedRoute>
                  <PortalOrders />
                </ProtectedRoute>
              } />
              <Route path="/portal/dashboard" element={
                <ProtectedRoute>
                  <PortalDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/orders" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminOrders />
                </ProtectedRoute>
              } />
              <Route path="/admin/models" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminModels />
                </ProtectedRoute>
              } />
              <Route path="/admin/customers" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminCustomers />
                </ProtectedRoute>
              } />
              <Route path="/admin/drafts" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminDrafts />
                </ProtectedRoute>
              } />
              <Route path="/admin/analytics" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminAnalytics />
                </ProtectedRoute>
              } />
              <Route path="/admin/analytics-enterprise" element={
                <ProtectedRoute requireAdmin={true}>
                  <AnalyticsEnterprise />
                </ProtectedRoute>
              } />
              <Route path="/admin/dashboard" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/ai-insights" element={
                <ProtectedRoute requireAdmin={true}>
                  <AIInsights />
                </ProtectedRoute>
              } />
              <Route path="/admin/reports" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdvancedReports />
                </ProtectedRoute>
              } />
              <Route path="/admin/financial" element={
                <ProtectedRoute requireAdmin={true}>
                  <Financial />
                </ProtectedRoute>
              } />
              <Route path="/admin/integrations" element={
                <ProtectedRoute requireAdmin={true}>
                  <Integrations />
                </ProtectedRoute>
              } />
              <Route path="/admin/security" element={
                <ProtectedRoute requireAdmin={true}>
                  <Security />
                </ProtectedRoute>
              } />
              <Route path="/admin/workflows" element={
                <ProtectedRoute requireAdmin={true}>
                  <Workflows />
                </ProtectedRoute>
              } />
              <Route path="/admin/monitoring" element={
                <ProtectedRoute requireAdmin={true}>
                  <Monitoring />
                </ProtectedRoute>
              } />
              <Route path="/admin/export" element={
                <ProtectedRoute requireAdmin={true}>
                  <DataExport />
                </ProtectedRoute>
              } />
              <Route path="/admin/content" element={
                <ProtectedRoute requireAdmin={true}>
                  <Content />
                </ProtectedRoute>
              } />
              <Route path="/admin/notifications" element={
                <ProtectedRoute requireAdmin={true}>
                  <Notifications />
                </ProtectedRoute>
              } />
              <Route path="/admin/policies" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminPolicies />
                </ProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <ProtectedRoute requireAdmin={true}>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              <Route path="/other-policies" element={<OtherPolicies />} />
                                </Routes>
                  </Suspense>
                </MobileSpacingWrapper>
              </div>
          <ConditionalFooter />
                  </div>
        </Router>
      </NetworkErrorHandler>
    </ErrorBoundary>
  )
}

export default App
